import {EventEmitter, Injectable} from '@angular/core';
import {PortalPageComponent} from "../../../pages/portal-page/portal-page.component";
import {PortalCrumb} from "./portal-crumb";
import {PortalBaseComponent} from "../../components/portal-base/portal-base.component";
import {PortalDataService} from "../portal-data/portal-data.service";
import {Router} from "@angular/router";
import {PortalNavigation} from "./portal-navigation.enum";
import {PortalPageModel} from "../portal-data/portal-page.model";
import {CatalogDataService} from "../catalog-data/catalog-data.service";
import {CatalogNavigationService} from "../../../pages/catalog-page/services/catalog-navigation/catalog-navigation.service";
import {PortalComponentModel} from "../portal-data/portal-component.model";
import {FieldService} from '../field/field.service';

/**
 * A service that wraps all the other services accessible from dynamic portal components.
 */
@Injectable({
  providedIn: 'root'
})
export class PortalService {

  pages: PortalPageComponent[] = [];

  components: PortalBaseComponent[] = [];

  stack: PortalPageModel[] = [];

  navigationEvent: EventEmitter<any> = new EventEmitter<any>();

  constructor(private router: Router,
              private portalDataService: PortalDataService,
              private catalogDataService: CatalogDataService,
              private catalogNavigationService: CatalogNavigationService,
              private fieldService: FieldService) {
  }

  init() {
  }

  registerComponent(comp: PortalBaseComponent) {
    this.components.includes(comp) || this.components.push(comp);
  }

  deregisterComponent(comp: PortalBaseComponent) {
    const i = this.components.indexOf(comp);
    if (i >= 0) {
      this.components.splice(i, 1);
    }
  }

  registerPage(comp: PortalPageComponent) {
    this.pages.includes(comp) || this.pages.push(comp);
  }

  deregisterPage(comp: PortalPageComponent) {
    const i = this.pages.indexOf(comp);
    if (i >= 0) {
      this.pages.splice(i, 1);
    }
  }

  getComponentsForPage(comp: PortalPageComponent): PortalBaseComponent[] {
    const pageWebform = this.getPageWebform(comp);
    return this.components.filter((c) => this.getComponentWebform(c) === pageWebform);
  }

  getPageForComponent(comp: PortalBaseComponent): PortalPageComponent {
    const componentWebform = this.getComponentWebform(comp);
    return this.pages.find((p) => componentWebform === this.getPageWebform(p));
  }

  navigateViaLink(id: string): PortalPageModel[] {
    return this.navigateToPage(id, PortalNavigation.Link);
  }

  navigateViaBreadcrumb(id: string): PortalPageModel[] {
    return this.navigateToPage(id, PortalNavigation.Breadcrumb);
  }

  navigate(id: string, route: boolean = true): PortalPageModel[] {
    return this.navigateToPage(id, PortalNavigation.Other, route);
  }

  getPage(id: string): PortalPageModel {
    const pages = this.portalDataService.getPages();

    return pages.find((d) => d.id === id);
  }

  getComponent(id: string): PortalComponentModel  {
    const components = this.portalDataService.getComponents();

    return components.find((c) => c.id === id);
  }

  getPortalDataService() {
    return this.portalDataService;
  }

  getCatalogDataService() {
    return this.catalogDataService;
  }

  getCatalogNavigationService() {
    return this.catalogNavigationService;
  }

  getBreadcrumb(): PortalCrumb[] {
    return this.stack.map((d) => {
      return {id: d.id, name: d.Name.displayValue, description: d.Description.displayValue}
    });
  }

  private getPageWebform(comp: PortalPageComponent) {
    return comp?.formRenderer?.formio || comp?.formBuilder?.builder?.instance?.webform;
  }

  private getComponentWebform(comp: PortalBaseComponent) {
    return comp?.formioComponent?.root;
  }

  private navigateToPage(id: string, kind: PortalNavigation = PortalNavigation.Other, route: boolean = true): PortalPageModel[] {
    const dashboards = this.portalDataService.getPages();
    const dashboard = dashboards.find((d) => d.id === id);

    if (dashboard) {
      if (kind === PortalNavigation.Other) {
        if (this.stack.length > 0 && this.stack[this.stack.length - 1].id === id) {
          console.debug(`PortalService.navigateToPage: already on dashboard ${id}`);
        } else {
          this.stack = [dashboard];
        }
      } else if (kind === PortalNavigation.Breadcrumb) {
        const i = this.stack.findIndex((d) => d.id === id);
        this.stack = i != -1 ? this.stack.slice(0, i + 1) : [dashboard];
      } else if (kind === PortalNavigation.Link) {
        if (this.stack.length > 0 && this.stack[this.stack.length - 1].id === id) {
          console.debug(`PortalService.navigateToPage: already on page ${id}`);
        } else {
          this.stack.push(dashboard);
        }
      }

      if (route) {
        this.router.navigateByUrl(`/Portal/${id}`);
      }
      this.navigationEvent.emit(true);
    }

    return this.stack;
  }

}
